import type { MarketplaceSettings } from '@ralph/contracts'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SectionHead } from '@/components/ui/admin-chrome.tsx'
import { Button, EmptyState, Input, Panel } from '@/components/ui/primitives.tsx'
import { useMarketplaceSettings, useSaveMarketplaceSettings } from '@/hooks/use-maker.ts'
import { describeApiError, useT } from '@/i18n/index.ts'

const MEGABYTE = 1024 * 1024

type Quotas = MarketplaceSettings['quotas']
type CountKey = Exclude<keyof Quotas, 'storagePerBaseBytes' | 'storagePerGroupBaseBytes'>
type StorageKey = 'storagePerBaseBytes' | 'storagePerGroupBaseBytes'

/** The two sets of limits (PRD-26 D-41), in the order each heading reads them. */
const PERSONAL: Array<CountKey | StorageKey> = [
  'agentsPerMaker',
  'basesPerMaker',
  'documentsPerBase',
  'storagePerBaseBytes',
  'previewRunsPerMakerPerDay'
]
const GROUP: Array<CountKey | StorageKey> = [
  'agentsPerGroup',
  'basesPerGroup',
  'documentsPerGroupBase',
  'storagePerGroupBaseBytes'
]

const isStorage = (key: keyof Quotas): key is StorageKey =>
  key === 'storagePerBaseBytes' || key === 'storagePerGroupBaseBytes'

/**
 * Marketplace policy, as an administrator edits it.
 *
 * Storage is entered in megabytes and stored in bytes: the contract's unit is bytes because
 * that is what a quota check compares, and no human should be asked to type 262144000. The
 * conversion is the only transformation in this form — everything else is the settings
 * object edited in place, so a field added to `MarketplaceSettingsSchema` shows up here as a
 * type error rather than as a silently dropped value.
 *
 * What is no longer here is deliberate (PRD-26): review thresholds and sensitive groups gave
 * way to one standing rule — sharing beyond the groups you manage needs an administrator — and
 * "who may create agents" gave way to everyone (D-13, D-22). A stored document that still
 * carries those keys is read with them ignored.
 *
 * Every value on this form is a number or a name the platform enforces, so every input is
 * set in mono. A quota typed into a proportional field is a quota nobody can line up against
 * the one below it.
 */
export const SettingsForm = ({ enabled }: { enabled: boolean }) => {
  // Read inside the body rather than at import: `const strings = settingsStrings` at module
  // scope is the capture that keeps rendering the previous language after a switch, with no
  // type error and no failing test (PRD-27 F-3, LOC-012).
  const messages = useT()
  const strings = messages.admin.settings
  const query = useMarketplaceSettings(enabled)
  const save = useSaveMarketplaceSettings()
  const [draft, setDraft] = useState<MarketplaceSettings | null>(null)

  useEffect(() => {
    if (query.data && draft === null) setDraft(query.data)
  }, [query.data, draft])

  if (query.isLoading) return <p className="text-[13px] text-fg-subtle">{strings.loading}</p>
  if (query.isError) return <EmptyState title={strings.failed} body={describeApiError(messages, query.error)} />
  if (!draft) return null

  const patch = (next: Partial<MarketplaceSettings>) => setDraft({ ...draft, ...next })
  const setQuota = (key: keyof Quotas, value: number) => patch({ quotas: { ...draft.quotas, [key]: value } })

  const quotaField = (key: CountKey | StorageKey) =>
    isStorage(key) ? (
      <NumberField
        key={key}
        id={`quota-${key}`}
        label={strings.quotaLabels[key]}
        value={Math.round(draft.quotas[key] / MEGABYTE)}
        min={1}
        onChange={(megabytes) => setQuota(key, Math.max(1, megabytes) * MEGABYTE)}
      />
    ) : (
      <NumberField
        key={key}
        id={`quota-${key}`}
        label={strings.quotaLabels[key]}
        value={draft.quotas[key]}
        min={1}
        onChange={(value) => setQuota(key, value)}
      />
    )

  return (
    <div>
      <SectionHead title={strings.title} lead={strings.lead} />

      <div className="mt-4 space-y-3">
        <Panel className="px-4 py-4">
          <StringList
            label={strings.categoriesTitle}
            hint={strings.categoriesHint}
            values={draft.categories}
            onChange={(categories) => patch({ categories })}
          />
        </Panel>

        <Panel className="px-4 py-4">
          <h3 className="text-[14px]">{strings.personalTitle}</h3>
          <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-fg-muted">{strings.personalHint}</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{PERSONAL.map(quotaField)}</div>
        </Panel>

        <Panel className="px-4 py-4">
          <h3 className="text-[14px]">{strings.groupTitle}</h3>
          <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-fg-muted">{strings.groupHint}</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{GROUP.map(quotaField)}</div>
        </Panel>

        <Panel className="px-4 py-4">
          <div className="max-w-xs">
            <NumberField
              id="archived-hold"
              label={strings.holdTitle}
              value={draft.archivedNameHoldDays}
              min={0}
              onChange={(archivedNameHoldDays) => patch({ archivedNameHoldDays })}
            />
          </div>
          <p className="mt-2 text-[12.5px] leading-relaxed text-fg-subtle">{strings.holdHint}</p>
        </Panel>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <Button variant="primary" size="sm" disabled={save.isPending} onClick={() => save.mutate(draft)}>
          {save.isPending ? strings.saving : strings.save}
        </Button>
        {save.isSuccess && (
          <span role="status" className="readout text-grounded">
            {strings.saved}
          </span>
        )}
        {save.isError && (
          <span role="alert" className="text-[12.5px] text-alert">
            {strings.saveFailed} {describeApiError(messages, save.error)}
          </span>
        )}
      </div>
    </div>
  )
}

const NumberField = ({
  id,
  label,
  value,
  min,
  max,
  onChange
}: {
  id: string
  label: string
  value: number
  min?: number
  max?: number
  onChange: (value: number) => void
}) => (
  <div>
    <label htmlFor={id} className="eyebrow mb-1 block">
      {label}
    </label>
    <Input
      id={id}
      type="number"
      min={min}
      max={max}
      value={value}
      className="readout"
      onChange={(event) => {
        const next = Number(event.target.value)
        if (Number.isFinite(next)) onChange(next)
      }}
    />
  </div>
)

/**
 * A set of names, edited as pills.
 *
 * Pills rather than a comma-separated field because every entry here is a category the
 * platform matches exactly: a stray space inside one string is invisible in a text field and
 * obvious as its own chip.
 */
const StringList = ({
  label,
  hint,
  values,
  onChange
}: {
  label: string
  hint: string
  values: string[]
  onChange: (values: string[]) => void
}) => {
  const strings = useT().admin.settings
  const [entry, setEntry] = useState('')

  const add = () => {
    const value = entry.trim()
    if (value === '' || values.includes(value)) return
    onChange([...values, value])
    setEntry('')
  }

  return (
    <div>
      <h3 className="text-[14px]">{label}</h3>
      <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-fg-muted">{hint}</p>

      <ul className="mt-2.5 flex flex-wrap gap-1.5">
        {values.map((value) => (
          <li
            key={value}
            className="readout inline-flex items-center gap-1 rounded-full border border-rule bg-card px-2.5 py-[3px]"
          >
            {value}
            <button
              type="button"
              aria-label={strings.remove(value)}
              onClick={() => onChange(values.filter((current) => current !== value))}
              className="-mr-0.5 rounded-full p-0.5 text-fg-subtle transition-colors hover:text-alert"
            >
              <X size={10} strokeWidth={2.6} />
            </button>
          </li>
        ))}
        {values.length === 0 && <li className="readout text-fg-subtle">{strings.empty}</li>}
      </ul>

      <div className="mt-2.5 flex gap-2">
        <Input
          value={entry}
          aria-label={strings.addTo(label)}
          className="h-[30px] max-w-xs text-[13px]"
          onChange={(event) => setEntry(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            add()
          }}
        />
        <Button size="sm" disabled={entry.trim() === ''} onClick={add}>
          {strings.add}
        </Button>
      </div>
    </div>
  )
}
